import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, Chip, Stack, Typography, useTheme } from '@mui/material';
import { Result } from '../../pages/rules/modules/resultEnum';
import { SuccessIcon, WarningIcon, NotRunIcon, ErrorIcon } from '../components/Icon';

const chipStyle = {
  padding: '0.25rem 0.5rem',
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '0.25rem'
};

interface EvaluateRunnerResultsRowProps {
  messageId: string;
  amount: number | string;
  icon: JSX.Element;
  handleClick: () => void;
  isActive: boolean;
}

const EvaluateRunnerResultsRow = (props: EvaluateRunnerResultsRowProps) => {
  const { messageId, icon, amount, handleClick, isActive } = props;
  const theme = useTheme();
  const usePalette = theme.palette;
  return (
    <Chip
      sx={{
        ...chipStyle,
        backgroundColor: isActive ? usePalette.primary.main : 'none',
        borderColor: usePalette.primary.dark,
        '&&:hover': {
          backgroundColor: isActive ? usePalette.primary.main : 'none'
        }
      }}
      icon={icon}
      onClick={() => handleClick()}
      variant="outlined"
      label={
        <Typography
          fontSize={theme.typography.fontSize}
          style={{
            color: isActive ? usePalette.secondary.light : usePalette.primary.main,
            fontWeight: isActive
              ? theme.typography.fontWeightRegular
              : theme.typography.fontWeightLight
          }}>
          <FormattedMessage id={messageId} />
          &nbsp; {amount}
        </Typography>
      }
    />
  );
};

interface TestrunnerResultsProps {
  successes: number;
  warnings: number;
  failures: number;
  notRun: number;
  evaluationDate?: Date;
  handleFilterClick: (result: Result, alreadyPressed: boolean) => void;
  selectedFilter: Result;
}

export const EvaluateRunnerResults = (props: TestrunnerResultsProps) => {
  const { successes, warnings, failures, notRun, handleFilterClick, selectedFilter } = props;
  const usePalette = useTheme().palette;
  const resultsData = [
    {
      result: Result.SUCCESS,
      messageId: 'TestSuccesses',
      value: successes,
      icon: (
        <SuccessIcon
          style={{
            fill: selectedFilter === Result.SUCCESS ? usePalette.primary.main : 'none',
            stroke:
              selectedFilter === Result.SUCCESS
                ? usePalette.secondary.light
                : usePalette.success.main
          }}
        />
      )
    },
    {
      result: Result.WARNING,
      messageId: 'TestWarnings',
      value: warnings,
      icon: (
        <WarningIcon
          style={{
            fill: selectedFilter === Result.WARNING ? usePalette.primary.main : 'none',
            stroke:
              selectedFilter === Result.WARNING
                ? usePalette.secondary.light
                : usePalette.warning.main
          }}
        />
      )
    },
    {
      result: Result.ERROR,
      messageId: 'TestFailures',
      value: failures,
      icon: (
        <ErrorIcon
          style={{
            fill: selectedFilter === Result.ERROR ? usePalette.primary.main : 'none',
            stroke:
              selectedFilter === Result.ERROR ? usePalette.secondary.light : usePalette.error.main
          }}
        />
      )
    },
    {
      result: Result.NOT_RUN,
      messageId: 'TestNotRun',
      value: notRun,
      icon: (
        <NotRunIcon
          style={{
            fill: selectedFilter === Result.NOT_RUN ? usePalette.primary.main : 'none',
            stroke:
              selectedFilter === Result.NOT_RUN
                ? usePalette.secondary.light
                : usePalette.primary.main
          }}
        />
      )
    }
  ];

  return (
    <Box padding={0.5} marginLeft={'10px'} alignContent={'center'}>
      <Stack direction={'row'} spacing={0.5} alignItems="center">
        {resultsData.map((data) => (
          <EvaluateRunnerResultsRow
            key={data.messageId}
            messageId={data.messageId}
            amount={data.value}
            icon={data.icon}
            handleClick={() => handleFilterClick(data.result, selectedFilter === data.result)}
            isActive={selectedFilter === data.result}
          />
        ))}
      </Stack>
    </Box>
  );
};
